import {Action, view, Icon, tokens} from '@sail/ui';
import {chevronRight, external} from '@sail/icons/react/Icon';
import {useIsAnyCryptoWalletConnectFlow} from 'src/v2/components/anyCrypto/useIsAnyCryptoWalletConnectFlow';
import {getStore} from 'src/v2/state/store';
import type {NetworkType} from 'src/v2/state/types';
import {Text, BrandedButton} from 'src/v2/components/common';
import type {StartWalletConnection} from 'src/v2/components/walletConnect/useIsWalletConnecting';
import {isNetworkDisabledForProvider} from 'src/v2/util/isNetworkDisabledForProvider';
import {getDisabledNetworksForProvider} from 'src/v2/util/isWalletProviderDisabled';
import {message, useIntl} from '@sail/intl';
import {buildNetworksFromSupportedCurrencies} from './buildNetworksFromSupportedCurrencies';
import {GaslessPromotion} from './GaslessPromotion';
import {NetworkIconsStack} from './NetworkIconsStack';

const messages = {
  connectWallet: message({
    id: 'crypto.payins.v2.network_selector.connect_wallet_button',
    description: 'Label for the connect wallet button',
    defaultMessage: 'Connect wallet',
  }),
};

const networkRowStyles = {
  stack: 'x',
  backgroundColor: 'offset',
  borderRadius: 'small',
  paddingX: 'space.150',
  paddingY: 'medium',
  alignY: 'center',
  distribute: 'space-between',
  width: 'fill',
} as const;

export const NetworkSelector = ({
  startWalletConnection,
}: {
  startWalletConnection: StartWalletConnection;
}) => {
  const store = getStore();
  const intl = useIntl();

  const isWalletConnecting = store.useSelector(
    (state) => state.overlay === 'CONNECT_WALLET',
  );

  const newWallet = store.useSelector((state) => state.newWallet);
  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );
  const disabledWalletProviders = store.useSelector(
    (state) => state.disabledWalletProviders,
  );
  if (!newWallet) {
    throw new Error('newWallet is required in NetworkSelector');
  }

  const isAnyCryptoWalletConnectFlow = useIsAnyCryptoWalletConnectFlow();
  const selectedAnyCryptoToken = store.useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const walletConnectSupportedCurrencies = store.useSelector(
    (state) => state.walletConnectSupportedCurrencies,
  );

  const disabledNetworksForWallet = getDisabledNetworksForProvider(
    newWallet.walletOption.provider,
    disabledWalletProviders,
  );

  const effectiveCurrencies = isAnyCryptoWalletConnectFlow
    ? walletConnectSupportedCurrencies.filter(
        (c) => c.assetCode === selectedAnyCryptoToken,
      )
    : supportedCurrencies;

  const filteredCurrencies = effectiveCurrencies.filter(
    (currency) => !disabledNetworksForWallet.includes(currency.currencyNetwork),
  );

  let networks = buildNetworksFromSupportedCurrencies(intl, filteredCurrencies);

  // Filter networks based on provider support
  networks = networks.filter(
    (network) =>
      !isNetworkDisabledForProvider(
        newWallet.walletOption.provider,
        network.networkType,
        disabledWalletProviders,
      ),
  );

  const handleNetworkSelect = async (networkType: NetworkType) => {
    store.setValue((state) => {
      if (state.newWallet) {
        state.newWallet.networkType = networkType;
      }
    });

    const {provider} = newWallet.walletOption;
    const params =
      provider === 'WalletConnect'
        ? {
            provider,
            networkType,
            walletKey: newWallet.walletOption.key,
          }
        : {
            provider,
            networkType,
          };
    await startWalletConnection(params);
  };

  const isSingleNetwork = networks.length === 1;

  if (isSingleNetwork) {
    const {networkType, label, icons} = networks[0];

    return (
      <view.div
        css={{stack: 'y', gapY: 'space.150'}}
        data-testid="network-selector"
      >
        {/* Only one network type -- the network row is not-clickable */}
        <view.div css={networkRowStyles}>
          <Text type="bodyEmphasized">{label}</Text>

          <view.div css={{stack: 'x', gap: 'space.150', alignY: 'center'}}>
            <NetworkIconsStack icons={icons} />
          </view.div>
        </view.div>

        <GaslessPromotion
          networkType={networkType}
          walletProvider={newWallet.walletOption.provider}
        />

        <BrandedButton
          onClick={() => handleNetworkSelect(networkType)}
          loading={isWalletConnecting}
          textCss={{gapX: '8px', stack: 'x', alignY: 'center'}}
        >
          <Icon icon={external} size="xsmall" />

          {intl.formatMessage(messages.connectWallet)}
        </BrandedButton>
      </view.div>
    );
  }

  return (
    <view.div
      css={{stack: 'y', gapY: 'space.150'}}
      data-testid="network-selector"
    >
      {networks.map(({networkType, label, icons}) => (
        <Action
          key={networkType}
          onPress={() => handleNetworkSelect(networkType)}
          disabled={isWalletConnecting}
          css={{
            ...networkRowStyles,
            ':hover': {
              backgroundColor: tokens.color.neutral[50].toString(),
            },
          }}
        >
          <view.div css={{stack: 'y', width: 'fill', gapY: 'xsmall'}}>
            <view.div
              css={{
                stack: 'x',
                alignY: 'center',
                distribute: 'space-between',
                width: 'fill',
              }}
            >
              <Text type="bodyEmphasized">{label}</Text>

              <view.div css={{stack: 'x', gap: 'space.150', alignY: 'center'}}>
                <NetworkIconsStack icons={icons} />
                <Icon icon={chevronRight} size="xsmall" />
              </view.div>
            </view.div>
            <GaslessPromotion
              networkType={networkType}
              walletProvider={newWallet.walletOption.provider}
            />
          </view.div>
        </Action>
      ))}
    </view.div>
  );
};
